import { motion } from "framer-motion";
import {
  Search,
  Filter,
  Users,
  MessageSquare,
  Calendar,
  AlertTriangle,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/utils/cn";

interface KanbanFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  showSlaWarning: boolean;
  onSlaWarningChange: (value: boolean) => void;
}

export function KanbanFilters({
  searchQuery,
  onSearchChange,
  showSlaWarning,
  onSlaWarningChange,
}: KanbanFiltersProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E2E8F0] bg-white px-6 py-4"
    >
      {/* Left Side - Search & Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text"
            placeholder="Buscar tickets..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-10 w-64 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] pl-10 pr-4 text-sm text-[#0F172A] placeholder:text-[#94A3B8] transition-all focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20"
          />
        </div>

        {/* Filter Dropdowns */}
        <FilterDropdown icon={Users} label="Agente" />
        <FilterDropdown icon={MessageSquare} label="Canal" />
        <FilterDropdown icon={Filter} label="Status" />
        <FilterDropdown icon={Calendar} label="Data" />
      </div>

      {/* Right Side - SLA Toggle */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => onSlaWarningChange(!showSlaWarning)}
          className={cn(
            "flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-all",
            showSlaWarning
              ? "bg-red-50 text-red-600 ring-2 ring-red-100"
              : "bg-[#F8FAFC] text-[#64748B] hover:bg-[#F1F5F9] border border-[#E2E8F0]"
          )}
        >
          <AlertTriangle className={cn("h-4 w-4", showSlaWarning && "animate-pulse")} />
          <span>SLA em Atraso</span>
          {showSlaWarning && (
            <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-xs font-bold text-red-600">
              3
            </span>
          )}
        </button>
      </div>
    </motion.div>
  );
}

interface FilterDropdownProps {
  icon: React.ElementType;
  label: string;
}

function FilterDropdown({ icon: Icon, label }: FilterDropdownProps) {
  return (
    <button className="flex items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-3 py-2.5 text-sm font-medium text-[#64748B] transition-all hover:bg-[#F8FAFC] hover:border-[#CBD5E1]">
      <Icon className="h-4 w-4" />
      <span>{label}</span>
      <ChevronDown className="h-3.5 w-3.5 text-[#94A3B8]" />
    </button>
  );
}